import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { VideoTile } from "@/components/VideoTile";
import { IMG } from "@/lib/images";
import { getJSON } from "@/lib/api";

const Label = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
    {children}
  </span>
);

const VERTICALS = ["All", "Real Estate", "Healthcare", "Legal", "Automotive", "Home Services"];
const TEAMS_FILTER = ["All", "Product", "Marketing", "Creative"];

export default function Customers() {
  const [vertical, setVertical] = useState("All");
  const [team, setTeam] = useState("All");

  const { data: projects = [] } = useQuery({
    queryKey: ["projects", "customers"],
    queryFn: () => getJSON("/projects?surface=customers"),
  });

  const filtered = useMemo(
    () =>
      projects.filter(
        (p) =>
          (vertical === "All" || p.vertical === vertical) &&
          (team === "All" || p.team === team),
      ),
    [projects, vertical, team],
  );

  const clients = useMemo(
    () => [...new Set(projects.map((p) => p.client).filter(Boolean))],
    [projects],
  );

  return (
    <>
      <Hero
        image={IMG.aboutTeam}
        eyebrow="OUR WORK — LIVE"
        title="Real businesses. Real outcomes."
        subline="Every piece below is work we built, run, and answer for — across the verticals we specialize in. Browse the proof."
        primary={{ label: "Install your department", to: "/audit" }}
        secondary={{ label: "See the work →", href: "#work" }}
      />

      {/* Clients strip */}
      {clients.length > 0 && (
        <section className="border-b border-black/5 bg-[var(--bg-base)] py-12" data-testid="customers-clients">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <Reveal>
              <p className="text-center text-xs font-semibold uppercase tracking-[0.16em] text-[var(--muted)]">
                Trusted by businesses moving fast
              </p>
            </Reveal>
            <Reveal delay={120}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
                {clients.map((c) => (
                  <span key={c} className="text-lg font-semibold tracking-tight text-[var(--ink)]/35 transition-colors hover:text-[var(--ink)]/70">
                    {c}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Intro + vertical filter */}
      <section id="work" className="bg-[var(--bg-base)] pt-24 md:pt-32" data-testid="customers-intro">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Label>Selected outcomes</Label></Reveal>
          <Reveal delay={100}>
            <p className="mt-5 max-w-2xl text-2xl font-semibold leading-snug tracking-[-0.02em] sm:text-3xl">
              The work, by industry. Each piece ships with a named human behind
              it — and the result attached.
            </p>
          </Reveal>
          <Reveal delay={160}>
            <div className="mt-10">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">By team</p>
              <div className="flex flex-wrap gap-2.5" data-testid="customers-team-filter">
                {TEAMS_FILTER.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTeam(t)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-all duration-300 ${
                      team === t
                        ? "border-[var(--ember)] bg-[var(--mint)] text-[var(--charcoal)]"
                        : "border-black/10 bg-[var(--bg-contrast)] text-[var(--ink)]/70 hover:border-[var(--ember)]/40 hover:text-[var(--ink)]"
                    }`}
                    data-testid={`customers-team-${t.toLowerCase()}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-7">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">By industry</p>
              <div className="flex flex-wrap gap-2.5" data-testid="customers-filter">
                {VERTICALS.map((v) => (
                  <button
                    key={v}
                    onClick={() => setVertical(v)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-all duration-300 ${
                      vertical === v
                        ? "border-[var(--ember)] bg-[var(--mint)] text-[var(--charcoal)]"
                        : "border-black/10 bg-[var(--bg-contrast)] text-[var(--ink)]/70 hover:border-[var(--ember)]/40 hover:text-[var(--ink)]"
                    }`}
                    data-testid={`customers-vertical-${v.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-[var(--bg-base)] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div key={`${team}-${vertical}`} className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6" data-testid="customers-gallery">
            {filtered.map((p, i) => (
              <VideoTile
                key={p.slug}
                project={p}
                delay={(i % 4) * 90}
                aspect={p.featured ? "aspect-video" : "aspect-[4/5]"}
                className={p.featured ? "md:col-span-2" : ""}
              />
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="py-10 text-center text-[var(--muted)]">No work in this industry yet.</p>
          )}
        </div>
      </section>

      <CtaBand />
    </>
  );
}
